import React, { useState, useEffect } from 'react';
import { Note } from '../../../types/Note';
import { noteService } from '../../../services/noteService';
import './NoteNavigation.css';

interface NoteNavigationProps {
  onNoteSelect: (note: Note) => void;
  userId: string;
}

interface YearFolder {
  year: number;
  months: {
    [key: number]: Note[];
  };
}

export const NoteNavigation: React.FC<NoteNavigationProps> = ({ onNoteSelect, userId }) => {
  const [folders, setFolders] = useState<YearFolder[]>([]);
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());
  const [expandedMonths, setExpandedMonths] = useState<{ [year: number]: Set<number> }>({});

  useEffect(() => {
    const loadNotes = async () => {
      try {
        const notes = await noteService.getUserNotes(userId);
        const organizedNotes = organizeNotesByYearAndMonth(notes);
        setFolders(organizedNotes);
      } catch (error) {
        console.error('Error loading notes:', error);
      }
    };

    loadNotes();
  }, [userId]);

  const organizeNotesByYearAndMonth = (notes: Note[]): YearFolder[] => {
    const yearMap = new Map<number, YearFolder>();

    notes.forEach((note) => {
      const date = new Date(note.createdAt);
      const year = date.getFullYear();
      const month = date.getMonth();

      if (!yearMap.has(year)) {
        yearMap.set(year, {
          year,
          months: {},
        });
      }

      const yearFolder = yearMap.get(year)!;
      if (!yearFolder.months[month]) {
        yearFolder.months[month] = [];
      }
      yearFolder.months[month].push(note);
    });

    return Array.from(yearMap.values()).sort((a, b) => b.year - a.year);
  };

  const toggleYear = (year: number) => {
    const newExpandedYears = new Set(expandedYears);
    if (newExpandedYears.has(year)) {
      newExpandedYears.delete(year);
    } else {
      newExpandedYears.add(year);
    }
    setExpandedYears(newExpandedYears);
  };

  const toggleMonth = (year: number, month: number) => {
    setExpandedMonths((prev) => {
      const yearSet = new Set(prev[year] || []);
      if (yearSet.has(month)) {
        yearSet.delete(month);
      } else {
        yearSet.add(month);
      }
      return { ...prev, [year]: yearSet };
    });
  };

  const getMonthName = (month: number): string => {
    return new Date(2000, month, 1).toLocaleString('default', { month: 'long' });
  };

  return (
    <div className="note-navigation">
      <h2>Notes</h2>
      <div className="note-navigation__folders">
        {folders.map((folder) => (
          <div key={folder.year} className="note-navigation__year">
            <div
              className="note-navigation__year-header"
              onClick={() => toggleYear(folder.year)}
            >
              <span className="note-navigation__year-icon">
                {expandedYears.has(folder.year) ? '▼' : '▶'}
              </span>
              {folder.year}
            </div>
            {expandedYears.has(folder.year) && (
              <div
                className="note-navigation__months"
                aria-hidden={!expandedYears.has(folder.year)}
              >
                {Object.entries(folder.months).map(([month, notes]) => (
                  <div key={month} className="note-navigation__month">
                    <div className="note-navigation__month-header" onClick={() => toggleMonth(folder.year, parseInt(month))} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                      <span className="note-navigation__month-icon" style={{ marginRight: 4 }}>
                        {expandedMonths[folder.year]?.has(parseInt(month)) ? '▼' : '▶'}
                      </span>
                      {getMonthName(parseInt(month))}
                    </div>
                    {expandedMonths[folder.year]?.has(parseInt(month)) && (
                      <div
                        className="note-navigation__notes"
                        aria-hidden={!expandedMonths[folder.year]?.has(parseInt(month))}
                      >
                        {notes.map((note) => (
                          <div
                            key={note.id}
                            className="note-navigation__note"
                            onClick={() => onNoteSelect(note)}
                          >
                            {note.title}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}; 